import { Badge, Button, Col, Row } from 'reactstrap';
import { SemanticModel, SemanticModelLogicalTable, SnowflakeDatabaseSchema } from '../../../../../../../api/initiative-data-integrations';
import { BasicAlert } from '@g17eco/molecules/alert';

interface TabTablesProps {
  semanticModel: SemanticModel;
  setSemanticModel: (semanticModel: SemanticModel) => void;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  schema?: SnowflakeDatabaseSchema;
  handleEditTable: (table: SemanticModelLogicalTable) => void;
}

export const TabTables = (props: TabTablesProps) => {
  const { semanticModel, setSemanticModel, setHasUnsavedChanges, schema, handleEditTable } = props;
  return (
    <div className='px-4'>
      <Row className='mb-3'>
        <Col lg={12}>
          <small className='text-muted'>Select which tables to include in your semantic model and configure them</small>
        </Col>
      </Row>

      {!schema || Object.keys(schema.schemas).length === 0 ? (
        <BasicAlert type='info'>
          <i className='fal fa-info-circle me-2' />
          No database schema available. Please ensure your connection is set up correctly.
        </BasicAlert>
      ) : (
        <div>
          {Object.entries(schema.schemas).map(([schemaName, schemaData]) => (
            <div key={schemaName} className='mb-4 border-bottom'>
              <h6 className='mb-3'>
                <i className='fal fa-database me-2' />
                Schema: {schemaName}
              </h6>

              {Object.entries(schemaData.tables).map(([tableName, tableData]) => {
                const existingTable = semanticModel.tables.find(
                  (t) => t.base_table.schema === schemaName && t.base_table.table === tableName,
                );
                const isIncluded = !!existingTable;
                const tableKey = `${schemaName}.${tableName}`;

                return (
                  <div key={tableKey} className='p-3 border-top background-ThemeNeutralsExtralight'>
                    <div className='d-flex align-items-center justify-content-between'>
                      <div className='form-check d-flex align-items-center'>
                        <input
                          type='checkbox'
                          className='form-check-input me-3'
                          id={`table-${tableKey}`}
                          checked={isIncluded}
                          onChange={() => {
                            if (isIncluded) {
                              // Remove table
                              const updatedTables = semanticModel.tables.filter(
                                (t) => !(t.base_table.schema === schemaName && t.base_table.table === tableName),
                              );
                              setSemanticModel({ ...semanticModel, tables: updatedTables });
                            } else {
                              // Add table with defaults
                              const newTable: SemanticModelLogicalTable = {
                                name: tableName,
                                base_table: {
                                  database: schema.database_name,
                                  schema: schemaName,
                                  table: tableName,
                                },
                                description: tableData.description || '',
                                synonyms: [],
                                dimensions: [],
                                time_dimensions: [],
                                facts: [],
                                metrics: [],
                                filters: [],
                              };
                              setSemanticModel({
                                ...semanticModel,
                                tables: [...semanticModel.tables, newTable],
                              });
                            }
                            setHasUnsavedChanges(true);
                          }}
                        />
                        <div>
                          <label htmlFor={`table-${tableKey}`} className='form-check-label mb-0'>
                            <strong>{tableName}</strong>
                          </label>
                          {tableData.description && <div className='small text-muted'>{tableData.description}</div>}
                          <div className='small text-muted'>
                            {Object.keys(tableData.columns).length} columns available
                          </div>
                        </div>
                      </div>

                      <div className='d-flex align-items-center'>
                        {isIncluded ? (
                          <>
                            <div className='me-3 d-flex gap-2'>
                              <Badge color='info' pill title='Dimensions'>
                                D: {existingTable?.dimensions?.length || 0}
                              </Badge>
                              <Badge color='warning' pill title='Time Dimensions'>
                                T: {existingTable?.time_dimensions?.length || 0}
                              </Badge>
                              <Badge color='success' pill title='Facts'>
                                F: {existingTable?.facts?.length || 0}
                              </Badge>
                            </div>
                            <Button color='primary' size='sm' outline onClick={() => handleEditTable(existingTable)}>
                              <i className='fal fa-cog me-1' />
                              Configure
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>

                    {/* Show some column info */}
                    {isIncluded && (
                      <div className='mt-2 pt-2 border-top'>
                        <small className='text-muted'>
                          {existingTable?.dimensions?.length === 0 &&
                          existingTable?.time_dimensions?.length === 0 &&
                          existingTable?.facts?.length === 0 ? (
                            <span className='text-danger'>
                              <i className='fal fa-exclamation-triangle me-1' />
                              No columns configured yet. Click "Configure" to set up dimensions and facts.
                            </span>
                          ) : (
                            <span className='text-success'>
                              <i className='fal fa-check-circle me-1' />
                              {(existingTable?.dimensions?.length || 0) +
                                (existingTable?.time_dimensions?.length || 0) +
                                (existingTable?.facts?.length || 0)}{' '}
                              columns configured
                            </span>
                          )}
                        </small>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
